import {
  CaptureInfo,
  Polygon as CarbonPolygon,
  FixType,
  Point,
  PolygonRing,
} from "protos/geo/geo";
import {
  Polygon as GeoJsonPolygon,
  Geometry,
  GeometryCollection,
  Position,
} from "geojson";
import bearing from "@turf/bearing";
import destination from "@turf/destination";

/**
 * The list of fix types to exclude from locator service queries by default;
 * this should include all fix types which indicate that the reporter may not
 * have had a GNSS fix at the time the report was made.
 *
 * Note: this does not include FixType.FIX_TYPE_UNSPECIFIED because peplink
 * trackers report that fix type on all of their location updates.
 */
export const DEFAULT_EXCLUDE_FIX_TYPES: FixType[] = [
  FixType.NO_FIX,
  FixType.DEAD_RECKONING,
  FixType.IMPORTED,
];

export interface Bounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export type SimpleGeometry = Exclude<Geometry, GeometryCollection>;

/**
 * Converts a Geometry object to an array of "simple" Geometries (Geometries,
 * with the exception of GeometryCollection).  This is helpful to work around
 * some turf brokenness; see FarmLayers.tsx.
 *
 * @param geom a Geometry object
 * @returns an array of Geometry objects which are not GeometryCollections
 */
export const simpleGeometries = (geom: Geometry): SimpleGeometry[] => {
  switch (geom.type) {
    case "GeometryCollection": {
      return geom.geometries.flatMap((g) => simpleGeometries(g));
    }
    default: {
      return [geom];
    }
  }
};

/**
 * Given two points, calculate the line (LineAB)
 * and return a line segment that starts at pointA and ends at a distance of 'distanceMeters' such that
 * the returned line is colinear to LineAB
 */
export const destinationAlongLine = (
  pointA: Position,
  pointB: Position,
  lengthMeters: number
): [Position, Position] => {
  const lineBearing = bearing(pointA, pointB);
  const newEnd = destination(pointA, lengthMeters, lineBearing, {
    units: "meters",
  });
  return [pointA, newEnd.geometry.coordinates];
};

export function makeGeoId(): string {
  let id = "";
  const charset = [
    ..."0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ",
  ] as const;

  const NUM_WORDS = 3;
  // this shouldn't be more than 5 unless you start using Uint64s; each
  // character of base62 consumes 5.954 (lg 62) bits of entropy
  const CHARS_PER_WORD = 4;
  const entropy = new Uint32Array(NUM_WORDS);
  crypto.getRandomValues(entropy);

  for (let word = 0; word < NUM_WORDS; word++) {
    let x = entropy[word]!;

    for (let char = 0; char < CHARS_PER_WORD; char++) {
      id += charset[x % charset.length];
      x = Math.floor(x / charset.length);
    }
  }

  return id;
}

export function positionToPoint(
  position: Position,
  captureInfo: CaptureInfo | undefined
): Point {
  const id = makeGeoId();

  return {
    lng: position[0]!,
    lat: position[1]!,
    alt: position[2] ?? 0,
    captureInfo,
    id: { id },
    name: id,
  };
}

export function geoJsonPolygonToCarbonPolygon(
  gjPolygon: GeoJsonPolygon,
  captureInfo: CaptureInfo | undefined
): {
  pointDefs: Point[];
  polygon: CarbonPolygon;
} {
  const pointDefs: Point[] = [];
  const cPolygon: CarbonPolygon = { boundary: undefined, holes: [] };

  for (
    let ringIndex = 0;
    ringIndex < gjPolygon.coordinates.length;
    ringIndex++
  ) {
    const ringCoords = gjPolygon.coordinates[ringIndex];
    const cRing: PolygonRing = { points: [] };

    for (const posCoords of ringCoords!) {
      const pt = positionToPoint(posCoords, captureInfo);
      pointDefs.push(pt);

      // the portal API rejects zones with geometry that contains inline
      // coordinates, even if you also provide references to point
      // definitions; censor the inline coordinates on the way out.
      cRing.points.push({ ...pt, lat: 0, lng: 0, alt: 0 });
    }

    if (ringIndex === 0) {
      cPolygon.boundary = cRing;
    } else {
      cPolygon.holes.push(cRing);
    }
  }

  return {
    pointDefs,
    polygon: cPolygon,
  };
}

/**
 * Normalizes an azimuth in degrees to be within [0, 360) by adding or removing
 * turns as necessary.
 * @param heading a heading in degrees clockwise of north, but with no
 *   particular range.
 */
export function normalizeAzimuth(heading: number): number {
  while (heading < 0) {
    heading += 360;
  }

  while (heading > 360) {
    heading -= 360;
  }

  return heading;
}
